/**
 * Every compliance gap in the organisation, one queue: regulatory findings and
 * the gap suggestions (control amendments, missing documents). Each opens on
 * its own page.
 */
import { createFileRoute } from "@tanstack/react-router";

import { ComplianceOrganizationMissing, ComplianceUnavailable } from "../components/regulation/ComplianceUnavailable";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { PageHeader } from "../components/regulation/PageHeader";
import { FindingsQueuePage } from "../components/regulation/finding/FindingsQueue";
import { useFindingItems } from "../components/regulation/finding/useFindingItems";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { useOrgProfile } from "../state/orgProfile";

function FindingsFrame({ children }: { readonly children: React.ReactNode }) {
  return (
    <div data-regulation-page="true" className="relative flex min-h-0 flex-1 flex-col">
      <PageHeader title="Findings" />
      <div className="px-4 py-6 sm:px-6">{children}</div>
    </div>
  );
}

export function RegulationFindingsRoute() {
  const { org: orgSegment } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const view = useFindingItems(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  if (isLoading) return <FindingsFrame><ListSkeleton rows={6} /></FindingsFrame>;
  if (!organizationId) return <FindingsFrame><ComplianceOrganizationMissing /></FindingsFrame>;
  if (view.unavailableReason) {
    return (
      <FindingsFrame>
        <ComplianceUnavailable reason={view.unavailableReason} onRetry={view.refresh} />
      </FindingsFrame>
    );
  }
  if (!view.ready) return <FindingsFrame><ListSkeleton rows={6} /></FindingsFrame>;
  return (
    <FindingsQueuePage
      key={organizationId}
      organizationId={organizationId}
      orgSegment={orgSegment}
      items={view.items}
      people={view.people}
      peopleById={view.peopleById}
      personName={view.personName}
      canManage={canManage}
      actions={view.actions}
    />
  );
}

export const Route = createFileRoute("/$org/compliance/findings/")({ component: RegulationFindingsRoute });
